import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z as zod } from 'zod';
import { PencilSimple } from '@phosphor-icons/react/dist/ssr/PencilSimple';

import type { Meeting, MeetingType } from '@/types/meeting';
import { config } from '@/config';
import { dayjs } from '@/lib/v2/dayjs';
import axios from '@/lib/v2/axios';
import ProfessionalDialog from '@/components/common/professional-dialog';
import ProgressButton from '@/components/common/buttons/progress-button';
import { toTitleCase } from '@/utils/to-title-case';

const schema = zod
  .object({
    meeting_type_id: zod.number().min(1, 'Please select a meeting type'),
    held_on_date: zod
      .union([zod.string(), zod.date(), zod.null()])
      .refine((v) => v != null && v !== '' && dayjs(v).isValid(), 'Please select a date'),
    held_on_time: zod
      .union([zod.string(), zod.date(), zod.null()])
      .refine((v) => v != null && v !== '', 'Please select a time'),
    agenda: zod.string().min(10, 'Meeting agenda must be at least 10 characters'),
  })
  .refine(
    (data) => {
      if (!data.held_on_date || !data.held_on_time) return true;
      const date = dayjs(data.held_on_date);
      // Skip future-time validation when the meeting date is already in the past
      if (date.startOf('day').isBefore(dayjs().startOf('day'))) return true;
      const time = dayjs(data.held_on_time);
      const combined = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
      return combined.isAfter(dayjs().add(1, 'hour'), 'minute');
    },
    { message: 'Meeting must be scheduled at least 1 hour in advance', path: ['held_on_time'] }
  );

type Values = zod.infer<typeof schema>;

interface EditMeetingDialogProps {
  open: boolean;
  onClose: () => void;
  meeting: Meeting;
}

export function EditMeetingDialog({ open, onClose, meeting }: EditMeetingDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[] | null>(null);

  const isMeetingPast = meeting.held_on
    ? dayjs(meeting.held_on).startOf('day').isBefore(dayjs().startOf('day'))
    : false;

  const {
    control,
    handleSubmit,
    reset,
    register,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      meeting_type_id: meeting.meeting_type_id,
      held_on_date: meeting.held_on ? dayjs(meeting.held_on).toDate() : null,
      held_on_time: meeting.held_on ? dayjs(meeting.held_on).toDate() : null,
      agenda: meeting.agenda ?? '',
    },
  });

  // Reset form when dialog opens with fresh meeting data
  useEffect(() => {
    if (open) {
      reset({
        meeting_type_id: meeting.meeting_type_id,
        held_on_date: meeting.held_on ? dayjs(meeting.held_on).toDate() : null,
        held_on_time: meeting.held_on ? dayjs(meeting.held_on).toDate() : null,
        agenda: meeting.agenda ?? '',
      });
    }
  }, [open, meeting, reset]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${config.site.api}/api/v1/meetings/types`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        setMeetingTypes(response.data);
      } catch {
        setMeetingTypes([]);
        toast.error('Unable to fetch meeting types');
      }
    };
    fetchTypes();
  }, []);

  const updateMutation = useMutation({
    mutationFn: async (payload: { meeting_type_id: number; agenda: string; held_on: string }) => {
      const response = await axios.patch(`/api/v1/meetings/${meeting.id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Meeting updated');
      queryClient.invalidateQueries({ queryKey: ['meeting', String(meeting.id)] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to update meeting');
    },
  });

  const onSubmit = async (values: Values) => {
    const date = dayjs(values.held_on_date);
    const time = dayjs(values.held_on_time);
    const held_on = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    await updateMutation.mutateAsync({ meeting_type_id: values.meeting_type_id, agenda: values.agenda, held_on });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ProfessionalDialog
      actions={null}
      autoHeight
      contentSx={{ pt: 2 }}
      icon={<PencilSimple />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle="Update the meeting date, type, and agenda"
      title="Edit Meeting"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="meeting_type_id"
              render={({ field: { onChange, value } }) => {
                const selected = meetingTypes?.find((mt) => mt.id === value) ?? null;
                return (
                  <Autocomplete
                    fullWidth
                    getOptionLabel={(o) => toTitleCase(o.name)}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    onChange={(_, newValue) => onChange(newValue?.id ?? 0)}
                    options={meetingTypes ?? []}
                    value={selected}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={Boolean(errors.meeting_type_id)}
                        helperText={errors.meeting_type_id?.message}
                        label="Meeting Type"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {!meetingTypes ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    size="small"
                  />
                );
              }}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="held_on_date"
              render={({ field }) => (
                <DatePicker
                  label="Meeting Date"
                  disabled={isMeetingPast}
                  minDate={isMeetingPast ? undefined : dayjs()}
                  onChange={(v) => {
                    field.onChange(v ? dayjs(v).toDate() : null);
                    trigger(['held_on_date', 'held_on_time']);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.held_on_date),
                      helperText: isMeetingPast
                        ? 'Date cannot be changed after the meeting has passed'
                        : errors.held_on_date?.message,
                      size: 'small',
                    },
                  }}
                  value={field.value ? dayjs(field.value) : null}
                />
              )}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="held_on_time"
              render={({ field }) => (
                <TimePicker
                  label="Meeting Time"
                  disabled={isMeetingPast}
                  minutesStep={15}
                  onChange={(v) => {
                    field.onChange(v ? dayjs(v).toDate() : null);
                    trigger(['held_on_date', 'held_on_time']);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.held_on_time),
                      helperText: isMeetingPast ? undefined : (errors.held_on_time?.message || 'Select a time (e.g. 2:00 PM)'),
                      size: 'small',
                    },
                  }}
                  value={field.value ? dayjs(field.value) : null}
                  ampm
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              {...register('agenda')}
              error={Boolean(errors.agenda)}
              fullWidth
              helperText={errors.agenda?.message || 'Describe the meeting agenda and topics'}
              label="Meeting Agenda"
              multiline
              rows={4}
              size="small"
            />
          </Grid>
        </Grid>

        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <ProgressButton
            disabled={isSubmitting || updateMutation.isPending}
            loading={isSubmitting || updateMutation.isPending}
            loadingText="Saving..."
            onClick={() => handleSubmit(onSubmit)()}
            size="medium"
            text="Save Changes"
            type="button"
            variant="contained"
          />
        </Box>
      </form>
    </ProfessionalDialog>
  );
}
